import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setProfileError(null);
      return null;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, display_name, role, status, preferred_language, newsletter_opt_in, research_goals, affiliation')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      console.error('Failed to load profile:', error);
      setProfile(null);
      setProfileError(error.message);
      return null;
    }
    setProfile(data ?? null);
    setProfileError(data ? null : 'Profile row not found');
    return data ?? null;
  }, []);

  useEffect(() => {
    let mounted = true;
    let resolvedInitial = false;

    // The INITIAL_SESSION event fires after supabase-js finishes its async
    // initialisation (which includes parsing #access_token from the URL on a
    // access-link redirect). Relying on it — instead of getSession() — avoids
    // a race where ProtectedRoute redirects to /auth before the URL hash has
    // been processed. See https://supabase.com/docs/reference/javascript/auth-onauthstatechange
    //
    // Why this callback is NOT async / does NOT await loadProfile:
    // supabase-js's _notifyAllSubscribers does `await Promise.all(handlers)`
    // on every auth event. On tab refocus it fires SIGNED_IN as part of
    // _recoverAndRefresh, and while that's running every other supabase
    // request waits behind the auth client. If our handler awaited a
    // Supabase query (loadProfile), every page-level fetch would stall
    // for one extra round-trip — long enough on slow connections to trip
    // our 20 s timeout. We update session synchronously and fire-and-
    // forget the profile reload; its own setProfile triggers a render.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession ?? null);
      loadProfile(newSession?.user?.id).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[hc] loadProfile failed after auth event:', err);
      });
      if (!resolvedInitial) {
        resolvedInitial = true;
        setLoading(false);
      }
    });

    // Safety net: if no event has fired after 5s, unblock the UI so the user
    // sees the sign-in form rather than an indefinite "Loading…".
    const safetyTimer = setTimeout(() => {
      if (mounted && !resolvedInitial) {
        resolvedInitial = true;
        setLoading(false);
      }
    }, 5000);

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, [loadProfile]);

  // `metadata` (research_goals, affiliation) is only sent for registrations.
  // GoTrue stores it in auth.users.raw_user_meta_data when it creates the
  // user, and the handle_new_user trigger (migration 023) copies it onto the
  // profile row. For an existing account it is ignored.
  const signInWithEmail = useCallback(async (email, metadata = null) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        ...(metadata ? { data: metadata } : {}),
      },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const updateProfile = useCallback(
    async (patch) => {
      if (!session?.user?.id) throw new Error('Not signed in');
      const { data, error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', session.user.id)
        .select('id, email, display_name, role, status, preferred_language, newsletter_opt_in, research_goals, affiliation')
        .single();
      if (error) throw error;
      setProfile(data);
      return data;
    },
    [session],
  );

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      profileError,
      loading,
      isSignedIn: !!session,
      isAdmin: profile?.role === 'admin' && profile?.status === 'active',
      isActive: profile?.status === 'active',
      signInWithEmail,
      signOut,
      updateProfile,
      refreshProfile: () => loadProfile(session?.user?.id),
    }),
    [session, profile, profileError, loading, signInWithEmail, signOut, updateProfile, loadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
